import "./App.css";
import React, { Suspense, useEffect, useState } from "react";
import {
  Routes,
  Route,
  BrowserRouter,
  Navigate,
  useNavigate,
} from "react-router-dom";
import { Helmet } from "react-helmet";
import ProtectedRoute from "./protectedRoute";
import { useAppStore } from "./appStore";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import Snackbar from "@mui/material/Snackbar";
import Typography from "@mui/material/Typography";
import { Slide, ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import useInactivityLogout from "./utils/browserLogoutHandle";
import Loader from "./utils/loader";
import { base_url } from "./apiUtils";
import socket from "./socketClient"; // ⬅️ Import shared socket instance
import depositSocket from "./depositSocketClient";

// Temporarily disabled because payment requests now use the custom in-app
// alert dialog. Set this to true if native Windows/browser notifications are
// required again in the future.
const SHOW_SYSTEM_PAYMENT_NOTIFICATIONS = false;

const Accounts = React.lazy(() => import("./pages/Accounts/Accounts"));
const HierarchyWiseAccounts = React.lazy(
  () => import("./pages/Accounts/HierachyWiseAccounts/hierarachywiseAccounts"),
);

const HierarchyAccounts = React.lazy(
  () => import("./pages/HierarcyAccounts/HierarchyAccounts"),
);
const LowerGameDetails = React.lazy(
  () => import("./pages/Transactions/LowerGameDetails"),
);
const WalletStatementPage = React.lazy(
  () => import("./pages/Transactions/WalletStatements"),
);
const GameWalletStatementsPage = React.lazy(
  () => import("./pages/Transactions/GameWalletStatements"),
);
const SendPhoto = React.lazy(() => import("./pages/SendPhoto/SendPhoto"));
const EditMenuFormation = React.lazy(
  () => import("./pages/Menu/editMenuFormation"),
);
const TypistPage = React.lazy(() => import("./pages/Typist/TypistPage"));
const AddTime = React.lazy(() => import("./pages/AddTime/addTime"));
const AgentReportCardPage = React.lazy(
  () => import("./pages/AgentReportCard/agentReportCardPage"),
);
const UploadScanners = React.lazy(
  () => import("./pages/upload-scanner/uploadScanner"),
);
const GameWiseAccount = React.lazy(
  () => import("./pages/Accounts/gamewiseAccount/gameWiseAccount"),
);
const RecievePhotos = React.lazy(
  () => import("./pages/SendPhoto/RecievePhotos"),
);
const SelfBets = React.lazy(() => import("./pages/PlaceBets/SelfBets"));
const VoiceRecordPage = React.lazy(
  () => import("./pages/VoiceRecord/VoiceRecordPage"),
);
const UserDetailsReport = React.lazy(
  () => import("./pages/UserDetailsReport/UserDetailsReport"),
);
const Setup2FA = React.lazy(() => import("./utils/Setup2FA"));

const Dashboard = React.lazy(() => import("./pages/Dashboard/Dashboard"));
const Login = React.lazy(() => import("./pages/Login/login"));
const Roles = React.lazy(() => import("./pages/Roles/roles"));
const PageNotFound = React.lazy(
  () => import("./components/404page/pageNotFound"),
);
const Users = React.lazy(() => import("./pages/Users/users"));
const Register = React.lazy(() => import("./components/register/register"));
const MyProfile = React.lazy(() => import("./pages/Profile/myProfile"));
const Wallet = React.lazy(() => import("./pages/Wallet/wallet"));
const WithdrawRequest = React.lazy(
  () => import("./pages/Withdrawrequest.js/withdrawRequest"),
);
const DepositRequest = React.lazy(
  () => import("./pages/Depositrequest/depositRequest"),
);
const UserDetails = React.lazy(() => import("./pages/Users/userDetails"));
const WalletSettings = React.lazy(
  () => import("./pages/Wallet/walletSettings"),
);
const CommissionSettings = React.lazy(
  () => import("./pages/Settings/commissionSettings"),
);
const AppSettings = React.lazy(() => import("./pages/Settings/appSettings"));
const ChatbotPage = React.lazy(() => import("./pages/Chatbot/ChatbotPage"));
const AddGame = React.lazy(() => import("./pages/Game/addGame"));
const GameSelection = React.lazy(() => import("./pages/Game/gameSelection"));
const PlayGame = React.lazy(() => import("./pages/Game/playGame"));
const UpdateGame = React.lazy(() => import("./components/Game/UpdateGame"));
const CreateMenu = React.lazy(() => import("./pages/Menu/createMenu"));

const MenuFormation = React.lazy(() => import("./pages/Menu/menuFormation"));

const Menu = React.lazy(() => import("./pages/Game/menus"));
const WinningRatioSettings = React.lazy(
  () => import("./pages/Settings/winningRatioSettings"),
);
const RatioSettings = React.lazy(() => import("./pages/Menu/ratioSettings"));
const MenuSelection = React.lazy(() => import("./pages/Menu/menuSelection"));
const ShareList = React.lazy(
  () => import("./components/settings/share/shareList"),
);

const Results = React.lazy(() => import("./pages/Result/result"));
const ManualPayment = React.lazy(() => import("./pages/Result/manualPayment"));
const Holiday = React.lazy(() => import("./pages/Holiday/holiday"));
const PreClosingTimeList = React.lazy(
  () => import("./pages/Game/preClosingTimeList"),
);
const EditUserDetails = React.lazy(
  () => import("./pages/Users/editUserDetails"),
);
const Report = React.lazy(() => import("./pages/Reports/report"));
const PermissionsPage = React.lazy(
  () => import("./pages/Permissions/Permissions"),
);
const LoginManagement = React.lazy(
  () => import("./pages/LoginManagement/LoginManagement"),
);
const PlaceBets = React.lazy(() => import("./pages/PlaceBets/PlaceBets"));
const Messages = React.lazy(() => import("./pages/Message/Message"));
const Subscription = React.lazy(
  () => import("./components/subscription/Subscription"),
);
const Welcome = React.lazy(() => import("./pages/welcome/Welcome"));
const Unauthorized = React.lazy(
  () => import("./components/404page/Unauthorized"),
);
const Announcement = React.lazy(
  () => import("./pages/Annoucements/announcements"),
);

function App() {
  const apiUrl = `${base_url}wallet/get-deposit-requests`;
  const token = sessionStorage.getItem("accesstoken");
  const {
    setRequestedCount,
    profileData,
    setWalletBalance,
    userPermissions,
  } = useAppStore();
  // const navigate = useNavigate();
  const snackbarMessage = useAppStore((state) => state.snackbarMessage);
  const openSnackbar = useAppStore((state) => state.openSnackbar);
  const openSnackbarError = useAppStore((state) => state.openSnackbarError);
  const [incomingPaymentAlert, setIncomingPaymentAlert] = useState(null);
  const setopenSnackbarError = useAppStore(
    (state) => state.setOpenSnackbarError,
  );
  const setopenSnackbar = useAppStore((state) => state.setOpenSnackbar);

  const closeSnackbar = () => {
    setopenSnackbar(false);
  };
  const closeSnackbarError = () => {
    setopenSnackbarError(false);
  };

  useInactivityLogout();

  // const socket = io(socket_url);

  useEffect(() => {
    if (!profileData?._id || !token) return;

    if (!socket.connected) {
      socket.connect();
    }
    socket.emit("joinRoom", profileData._id);

    const handleWalletBalance = (data) => {
      if (data?.balance !== undefined) {
        setWalletBalance(data.balance);
      }
    };

    socket.on("walletBalance", handleWalletBalance);

    return () => {
      socket.off("walletBalance", handleWalletBalance);
    };
  }, [profileData?._id, token]);

  useEffect(() => {
    if (!token) return;

    const fetchRequestedCount = async () => {
      try {
        const urlWithParams = new URL(apiUrl);
        urlWithParams.searchParams.append("type", "deposit");

        const res = await fetch(urlWithParams.toString(), {
          method: "GET",
          headers: {
            accesstoken: token,
          },
        });

        const data = await res.json();

        const count = data?.requestedCount || 0;

        setRequestedCount(count);
      } catch (error) {}
    };

    fetchRequestedCount();

    const handleDepositRequestUpdate = () => {
      fetchRequestedCount();
    };

    if (!depositSocket.connected) {
      depositSocket.connect();
    }
    depositSocket.on("depositRequestUpdated", handleDepositRequestUpdate);

    return () => {
      depositSocket.off("depositRequestUpdated", handleDepositRequestUpdate);
    };
  }, [apiUrl, token]);

  useEffect(() => {
    if (!token || !profileData?._id) return;

    const activeAlerts = new Map();
    const activeNotifications = new Map();
    const alertAudio = new Audio("/mp3/notify1.wav");
    alertAudio.loop = true;
    alertAudio.preload = "auto";
    alertAudio.volume = 0.85;
    let voiceAlertTimer = null;

    const requestPermission = () => {
      if (
        SHOW_SYSTEM_PAYMENT_NOTIFICATIONS &&
        "Notification" in window &&
        Notification.permission === "default"
      ) {
        Promise.resolve(Notification.requestPermission()).catch(() => {});
      }
    };

    const playAlertSound = () => {
      if (activeAlerts.size === 0 || !alertAudio.paused) return;
      alertAudio.play().catch(() => {
        // Browsers can block audio until the admin interacts with the page.
      });
    };

    const announcePendingRequests = () => {
      if (
        activeAlerts.size === 0 ||
        !("speechSynthesis" in window) ||
        window.speechSynthesis.speaking
      ) {
        return;
      }

      const pendingRequests = Array.from(activeAlerts.values());
      const depositCount = pendingRequests.filter(
        (request) => request.requestType === "deposit",
      ).length;
      if (depositCount === 0) return;

      const message =
        depositCount === 1
          ? "New deposit request received"
          : `${depositCount} new deposit requests received`;

      const announcement = new SpeechSynthesisUtterance(
        `${message}. Please check the admin panel.`,
      );
      announcement.rate = 0.92;
      announcement.pitch = 1;
      announcement.volume = 1;
      announcement.onstart = () => {
        alertAudio.volume = 0.25;
      };
      announcement.onend = () => {
        alertAudio.volume = 0.85;
      };
      announcement.onerror = () => {
        alertAudio.volume = 0.85;
      };
      window.speechSynthesis.speak(announcement);
    };

    const startVoiceAlerts = () => {
      announcePendingRequests();
      if (!voiceAlertTimer) {
        voiceAlertTimer = window.setInterval(announcePendingRequests, 9000);
      }
    };

    const stopPaymentAlert = (requestId) => {
      if (!requestId) return;

      activeAlerts.delete(String(requestId));
      activeNotifications.get(String(requestId))?.close();
      activeNotifications.delete(String(requestId));

      // Keep the alert queue deposit-only.
      activeAlerts.forEach((alert, alertId) => {
        if (alert?.requestType !== "deposit") {
          activeAlerts.delete(alertId);
          activeNotifications.get(alertId)?.close();
          activeNotifications.delete(alertId);
        }
      });

      setIncomingPaymentAlert((currentAlert) => {
        if (String(currentAlert?.requestId || "") !== String(requestId)) {
          return currentAlert?.requestType === "deposit" ? currentAlert : null;
        }

        const remainingAlerts = Array.from(activeAlerts.values()).filter(
          (alert) => alert?.requestType === "deposit",
        );
        return remainingAlerts[remainingAlerts.length - 1] || null;
      });

      if (activeAlerts.size === 0) {
        alertAudio.pause();
        alertAudio.currentTime = 0;
        if (voiceAlertTimer) {
          window.clearInterval(voiceAlertTimer);
          voiceAlertTimer = null;
        }
        if ("speechSynthesis" in window) {
          window.speechSynthesis.cancel();
        }
      }
    };

    const retryBlockedSound = () => {
      if (activeAlerts.size > 0) {
        playAlertSound();
        startVoiceAlerts();
      }
    };

    requestPermission();
    document.addEventListener("pointerdown", requestPermission, { once: true });
    document.addEventListener("keydown", requestPermission, { once: true });
    document.addEventListener("pointerdown", retryBlockedSound);
    document.addEventListener("keydown", retryBlockedSound);

    const handleNewPaymentRequest = (request) => {
      const isDeposit = request?.requestType === "deposit";

      if (!isDeposit) return;
      if (request?.isDirectSystemOwnerCustomer !== true) return;

      const hasDepositRequestPermission = userPermissions?.some(
        (permission) =>
          permission?.path === "/depositrequest" &&
          permission?.access !== false,
      );

      // Deposit alerts are restricted to admins who were explicitly assigned
      // the Deposit Request permission. System owners and other roles must not
      // receive them implicitly.
      if (
        isDeposit &&
        (profileData?.role !== "admin" || !hasDepositRequestPermission)
      ) {
        return;
      }

      const canReceiveAllRequests = ["systemowner", "admin"].includes(
        profileData?.role,
      );
      const isIntendedRecipient =
        String(request?.raisedTo || "") === String(profileData?._id);

      if (!canReceiveAllRequests && !isIntendedRecipient) return;
      if (!request?.requestId) return;

      const notificationKey = `payment-request-notified:${request.requestId}`;
      if (sessionStorage.getItem(notificationKey)) return;
      sessionStorage.setItem(notificationKey, "1");

      const customerDetails = [
        request?.customer?.name,
        request?.customer?.userId,
        request?.customer?.phone,
      ]
        .filter(Boolean)
        .join(" • ");
      const amount = Number(request?.amount || 0).toLocaleString("en-IN", {
        maximumFractionDigits: 2,
      });
      const extraDetails = [
        request?.customer?.customerType,
        request?.paymentMode,
      ]
        .filter(Boolean)
        .join(" • ");

      activeAlerts.set(String(request.requestId), request);
      setIncomingPaymentAlert(request);
      playAlertSound();
      startVoiceAlerts();

      if (
        SHOW_SYSTEM_PAYMENT_NOTIFICATIONS &&
        "Notification" in window &&
        Notification.permission === "granted"
      ) {
        const notification = new Notification("New Deposit Request", {
          body: `${customerDetails}\n₹${amount}${extraDetails ? ` • ${extraDetails}` : ""}\nClick to review`,
          icon: "/fav-sm-logo.png",
          badge: "/fav-sm-logo.png",
          tag: `${request.requestType}-${request.requestId}`,
          renotify: true,
          requireInteraction: true,
          silent: true,
        });

        activeNotifications.set(String(request.requestId), notification);
        notification.onclick = () => {
          stopPaymentAlert(request.requestId);
          window.focus();
          window.location.assign("/depositrequest");
        };
      }
    };

    const handlePaymentRequestUpdate = (request) => {
      if (
        request?.requestId &&
        (request?.operationType === "delete" ||
          (request?.status && request.status !== "requested"))
      ) {
        stopPaymentAlert(request.requestId);
      }
    };

    const handlePaymentAlertAction = (event) => {
      const requestId = event?.detail?.requestId;
      const action = event?.detail?.action;
      const request = activeAlerts.get(String(requestId));
      if (!request) return;

      stopPaymentAlert(requestId);
      if (action === "accept") {
        window.location.assign("/depositrequest");
      }
    };

    if (!depositSocket.connected) {
      depositSocket.connect();
    }
    depositSocket.on("newPaymentRequest", handleNewPaymentRequest);
    depositSocket.on("paymentRequestUpdated", handlePaymentRequestUpdate);
    window.addEventListener("paymentAlertAction", handlePaymentAlertAction);

    return () => {
      document.removeEventListener("pointerdown", requestPermission);
      document.removeEventListener("keydown", requestPermission);
      document.removeEventListener("pointerdown", retryBlockedSound);
      document.removeEventListener("keydown", retryBlockedSound);
      depositSocket.off("newPaymentRequest", handleNewPaymentRequest);
      depositSocket.off("paymentRequestUpdated", handlePaymentRequestUpdate);
      window.removeEventListener(
        "paymentAlertAction",
        handlePaymentAlertAction,
      );
      activeNotifications.forEach((notification) => notification.close());
      setIncomingPaymentAlert(null);
      alertAudio.pause();
      alertAudio.currentTime = 0;
      if (voiceAlertTimer) window.clearInterval(voiceAlertTimer);
      if ("speechSynthesis" in window) window.speechSynthesis.cancel();
    };
  }, [token, profileData?._id, profileData?.role, userPermissions]);

  const handleIncomingPaymentAction = (action) => {
    if (
      incomingPaymentAlert?.requestType !== "deposit" ||
      !incomingPaymentAlert?.requestId
    ) {
      setIncomingPaymentAlert(null);
      return;
    }
    window.dispatchEvent(
      new CustomEvent("paymentAlertAction", {
        detail: {
          action,
          requestId: incomingPaymentAlert.requestId,
        },
      }),
    );
  };

  return (
    <>
      {/* Fixed card at the top showing count if > 0 */}

      {incomingPaymentAlert && (
      <Dialog
        open
        disableEscapeKeyDown
        BackdropProps={{
          sx: {
            backgroundColor: "rgba(8, 12, 35, 0.72)",
            backdropFilter: "blur(5px)",
          },
        }}
        PaperProps={{
          sx: {
            width: "calc(100% - 32px)",
            maxWidth: 430,
            overflow: "hidden",
            borderRadius: 4,
            boxShadow: "0 28px 80px rgba(0,0,0,0.4)",
          },
        }}
      >
        <Box
          sx={{
            px: 3,
            py: 2.5,
            color: "#fff",
            textAlign: "center",
            background: "linear-gradient(135deg, #126b45, #1c9a64)",
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr auto 1fr",
              alignItems: "center",
              mb: 0.6,
              color: "rgba(255,255,255,0.82)",
              fontSize: "0.72rem",
              fontWeight: 700,
              letterSpacing: 0.4,
            }}
          >
            <Box component="span" sx={{ textAlign: "left" }}>
              {incomingPaymentAlert?.createdAt
                ? new Date(
                    incomingPaymentAlert.createdAt,
                  ).toLocaleDateString("en-GB")
                    .replaceAll("/", "-")
                : ""}
            </Box>
            <Typography
              component="span"
              sx={{
                fontSize: "0.72rem",
                fontWeight: 800,
                letterSpacing: 1.4,
                textTransform: "uppercase",
              }}
            >
              New payment request
            </Typography>
            <Box component="span" sx={{ textAlign: "right" }}>
              {incomingPaymentAlert?.createdAt
                ? new Date(
                    incomingPaymentAlert.createdAt,
                  ).toLocaleTimeString("en-IN", {
                    hour: "2-digit",
                    minute: "2-digit",
                    hour12: true,
                  })
                : ""}
            </Box>
          </Box>
          <Typography variant="h5" sx={{ mt: 0.5, fontWeight: 800 }}>
            Deposit Request Received
          </Typography>
        </Box>

        <DialogContent sx={{ px: 3, pt: 3, pb: 2 }}>
          <Typography
            align="center"
            sx={{ color: "#697083", fontSize: "0.85rem" }}
          >
            {[
              incomingPaymentAlert?.customer?.name,
              incomingPaymentAlert?.customer?.phone,
            ]
              .filter(Boolean)
              .join(" • ")}
          </Typography>
          <Typography
            align="center"
            sx={{
              mt: 1,
              color: "#171b44",
              fontSize: "2rem",
              fontWeight: 900,
            }}
          >
            ₹
            {Number(incomingPaymentAlert?.amount || 0).toLocaleString("en-IN", {
              maximumFractionDigits: 2,
            })}
          </Typography>
          <Typography
            align="center"
            sx={{ mt: 1, color: "#8a90a0", fontSize: "0.78rem" }}
          >
            Choose Accept to open and review this request.
          </Typography>
        </DialogContent>

        <DialogActions sx={{ gap: 1.5, px: 3, pb: 3 }}>
          <Button
            fullWidth
            variant="contained"
            onClick={() => handleIncomingPaymentAction("cancel")}
            sx={{
              py: 1.15,
              borderRadius: 2.5,
              color: "#fff",
              fontWeight: 800,
              textTransform: "none",
              backgroundColor: "#d84343",
              "&:hover": { backgroundColor: "#bb3030" },
            }}
          >
            Cancel
          </Button>
          <Button
            fullWidth
            variant="contained"
            onClick={() => handleIncomingPaymentAction("accept")}
            sx={{
              py: 1.15,
              borderRadius: 2.5,
              color: "#fff",
              fontWeight: 800,
              textTransform: "none",
              backgroundColor: "#168451",
              "&:hover": { backgroundColor: "#0f6d41" },
            }}
          >
            Accept & View
          </Button>
        </DialogActions>
      </Dialog>
      )}

      <BrowserRouter>
        <Helmet defaultTitle="S - M ( M-India )">
          {/* <link rel="icon" href="/logo-vector.png" /> */}
        </Helmet>
        <ToastContainer
          position={"top-right"}
          theme="dark"
          hideProgressBar={true}
          autoClose={2000}
          transition={Slide}
        />

        <Suspense
          fallback={
            <div>
              <Loader />
            </div>
          }
        >
          <Routes>
            <Route path="/" element={<Login />} />
            <Route path="/welcome" element={<Welcome />} />
            <Route path="/chatbot" element={<ChatbotPage />} />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute requiredPermissions={["/dashboard"]}>
                  <Dashboard />
                </ProtectedRoute>
              }
            />

            <Route
              path="/users"
              element={
                <ProtectedRoute requiredPermissions={["/users"]}>
                  <Users />
                </ProtectedRoute>
              }
            />

            <Route path="/auth/verify-2fa" element={<Setup2FA />} />

            <Route
              path="/roles"
              element={
                <ProtectedRoute>
                  <Roles />
                </ProtectedRoute>
              }
            />

            <Route
              path="/users/register"
              element={
                <ProtectedRoute requiredPermissions={["/users/register"]}>
                  <Register />
                </ProtectedRoute>
              }
            />

            <Route
              path="/myprofile"
              element={
                <ProtectedRoute>
                  <MyProfile />
                </ProtectedRoute>
              }
            />

            <Route
              path="/users/edit-user-details"
              element={
                <ProtectedRoute>
                  <EditUserDetails />
                </ProtectedRoute>
              }
            />

            <Route
              path="/wallet"
              element={
                <ProtectedRoute requiredPermissions={["/wallet"]}>
                  <Wallet />
                </ProtectedRoute>
              }
            />

            <Route
              path="/walletsettings"
              element={
                <ProtectedRoute>
                  <WalletSettings />
                </ProtectedRoute>
              }
            />

            <Route
              path="/commissionsettings"
              element={
                <ProtectedRoute>
                  <CommissionSettings />
                </ProtectedRoute>
              }
            />

            <Route
              path="/share-settings"
              element={
                <ProtectedRoute>
                  <ShareList />
                </ProtectedRoute>
              }
            />

            <Route
              path="/share-list"
              element={<Navigate to="/share-settings" replace />}
            />

            <Route
              path="/update-share"
              element={<Navigate to="/share-settings" replace />}
            />

            <Route
              path="/winning-ratio-settings"
              element={
                <ProtectedRoute>
                  <WinningRatioSettings />
                </ProtectedRoute>
              }
            />

            <Route
              path="/ratio-settings"
              element={
                <ProtectedRoute requiredPermissions={["/ratio-settings"]}>
                  <RatioSettings />
                </ProtectedRoute>
              }
            />

            <Route
              path="/withdrawrequest"
              element={
                <ProtectedRoute requiredPermissions={["/withdrawrequest"]}>
                  <WithdrawRequest />
                </ProtectedRoute>
              }
            />

            <Route
              path="/depositrequest"
              element={
                <ProtectedRoute requiredPermissions={["/depositrequest"]}>
                  <DepositRequest />
                </ProtectedRoute>
              }
            />

            <Route
              path="/users/userdetails"
              element={
                <ProtectedRoute>
                  <UserDetails />
                </ProtectedRoute>
              }
            />

            <Route
              path="/add-time"
              element={
                <ProtectedRoute>
                  <AddTime />
                </ProtectedRoute>
              }
            />

            {/* <Route
              path="/reports"
              element={
               <ProtectedRoute requiredPermissions={[
                 
                   "/users",
                   "/selectMenu",
                   "/playGame",
                   "/wallet",
                   "/depositrequest",
                   "/withdrawrequest",
                   "/reports",
                   "/place-bets",
                 ]}>
                 <UserReport />
               </ProtectedRoute>
              }
              /> */}
            <Route
              path="/reports"
              element={
                <ProtectedRoute requiredPermissions={["/reports"]}>
                  <Report />
                </ProtectedRoute>
              }
            />

            <Route
              path="/addGame"
              element={
                <ProtectedRoute>
                  <AddGame />
                </ProtectedRoute>
              }
            />

            <Route
              path="/edit-games"
              element={
                <ProtectedRoute>
                  <GameSelection />
                </ProtectedRoute>
              }
            />

            {/* <Route
               path="/playGame"
               element={
                 <ProtectedRoute requiredPermissions={["/playGame"]}>
                   <PlayGame />
                 </ProtectedRoute>
               }
              /> */}
            <Route
              path="/addMenu"
              element={
                <ProtectedRoute requiredPermissions={["/addMenu"]}>
                  <CreateMenu />
                </ProtectedRoute>
              }
            />

            <Route
              path="/selectMenu"
              element={
                <ProtectedRoute requiredPermissions={["/selectMenu"]}>
                  <MenuSelection />
                </ProtectedRoute>
              }
            />

            <Route
              path="/menu-formation"
              element={
                <ProtectedRoute requiredPermissions={["/menu-formation"]}>
                  <MenuFormation />
                </ProtectedRoute>
              }
            />

            <Route
              path="/edit-menu-formation"
              element={
                <ProtectedRoute requiredPermissions={["edit-menu-formation"]}>
                  <EditMenuFormation />
                </ProtectedRoute>
              }
            />

            <Route
              path="/annoucements"
              element={
                <ProtectedRoute requiredPermissions={["/annoucements"]}>
                  <Announcement />
                </ProtectedRoute>
              }
            />

            <Route
              path="/edit-game-details/:id"
              element={
                <ProtectedRoute>
                  <UpdateGame />
                </ProtectedRoute>
              }
            />

            <Route
              path="/game/:name"
              element={
                <ProtectedRoute>
                  <Menu />
                </ProtectedRoute>
              }
            />

            <Route
              path="/result-declaration"
              element={
                <ProtectedRoute requiredPermissions={["/result-declaration"]}>
                  <Results />
                </ProtectedRoute>
              }
            />

            <Route
              path="/manual-payment"
              element={
                <ProtectedRoute requiredPermissions={["/manual-payment"]}>
                  <ManualPayment />
                </ProtectedRoute>
              }
            />

            <Route
              path="/holiday"
              element={
                <ProtectedRoute requiredPermissions={["/holiday"]}>
                  <Holiday />
                </ProtectedRoute>
              }
            />

            <Route
              path="/preclosing-time-list"
              element={
                <ProtectedRoute requiredPermissions={["/preclosing-time-list"]}>
                  <PreClosingTimeList />
                </ProtectedRoute>
              }
            />

            <Route
              path="/permissions"
              element={
                <ProtectedRoute requiredPermissions={["/permissions"]}>
                  <PermissionsPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/place-bets"
              element={
                <ProtectedRoute requiredPermissions={["/place-bets"]}>
                  <PlaceBets />
                </ProtectedRoute>
              }
            />

            <Route
              path="/self-bets"
              element={
                <ProtectedRoute requiredPermissions={["/self-bets"]}>
                  <SelfBets />
                </ProtectedRoute>
              }
            />

            <Route
              path="/send-voice-note"
              element={
                <ProtectedRoute requiredPermissions={["/send-voice-note"]}>
                  <VoiceRecordPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/send-message"
              element={
                <ProtectedRoute requiredPermissions={["/send-message"]}>
                  <Messages />
                </ProtectedRoute>
              }
            />

            <Route
              path="/accounts"
              element={
                <ProtectedRoute requiredPermissions={["/accounts"]}>
                  <Accounts />
                </ProtectedRoute>
              }
            />

            <Route
              path="/hierarchy-syso-accounts"
              element={
                <ProtectedRoute
                  requiredPermissions={["/hierarchy-syso-accounts"]}
                >
                  <HierarchyAccounts />
                </ProtectedRoute>
              }
            />

            <Route
              path="/lower-game-play-details"
              element={
                <ProtectedRoute
                  requiredPermissions={["/lower-game-play-details"]}
                >
                  <LowerGameDetails />
                </ProtectedRoute>
              }
            />

            <Route
              path="/wallet-statements"
              element={
                <ProtectedRoute requiredPermissions={["/wallet-statements"]}>
                  <WalletStatementPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/game-wallet-statements"
              element={
                <ProtectedRoute
                  requiredPermissions={["/game-wallet-statements"]}
                >
                  <GameWalletStatementsPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/send-photo"
              element={
                <ProtectedRoute requiredPermissions={["/send-photo"]}>
                  <SendPhoto />
                </ProtectedRoute>
              }
            />

            <Route
              path="/recieved-photos"
              element={
                <ProtectedRoute requiredPermissions={["/recieved-photos"]}>
                  <RecievePhotos />
                </ProtectedRoute>
              }
            />

            <Route
              path="/typist"
              element={
                <ProtectedRoute requiredPermissions={["/typist"]}>
                  <TypistPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/agent-report-card"
              element={<AgentReportCardPage />}
            />

            <Route
              path="/upload-scanner"
              element={
                <ProtectedRoute requiredPermissions={["/upload-scanner"]}>
                  <UploadScanners />
                </ProtectedRoute>
              }
            />

            <Route
              path="/game-wise-accounts"
              element={
                <ProtectedRoute requiredPermissions={["/game-wise-accounts"]}>
                  <GameWiseAccount />
                </ProtectedRoute>
              }
            />

            <Route
              path="/hierarchy-wise-accounts"
              element={
                <ProtectedRoute
                  requiredPermissions={["/hierarchy-wise-accounts"]}
                >
                  <HierarchyWiseAccounts />
                </ProtectedRoute>
              }
            />

            <Route
              path="/user-detail-report"
              element={
                <ProtectedRoute requiredPermissions={["/user-detail-report"]}>
                  <UserDetailsReport />
                </ProtectedRoute>
              }
            />

            <Route
              path="/appsettings"
              element={
                <ProtectedRoute requiredPermissions={["/appsettings"]}>
                  <AppSettings />
                </ProtectedRoute>
              }
            />

            <Route path="/" />
            <Route path="*" element={<PageNotFound />} />
            <Route path="/unauthorized" element={<Unauthorized />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
      <Snackbar
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={closeSnackbar}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
      >
        <Alert
          onClose={closeSnackbar}
          severity="success"
          sx={{
            width: "100%",
            backgroundColor: "darkgreen",
            color: "#fff",
            height: "50px",
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
      <Snackbar
        open={openSnackbarError}
        autoHideDuration={3000}
        onClose={closeSnackbarError}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Alert
          onClose={closeSnackbarError}
          severity="error"
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
      {/* <Subscription /> */}
    </>
  );
}

export default App;
